import { Platform, StyleSheet, Text, View } from "react-native";
import LPressable from "@components/LPressable";
import { ISLAND_EDGE, ISLAND_FIELDS_HEIGHT } from "../island/dimensions";
import { walletOf } from "../walletKind";
import { Chevron } from "./Chevron";
import { CONTROL_HEIGHT, RADIUS, colors } from "./colors";
import { DestinationChip } from "./DestinationChip";
import { OrderSummaryShape } from "./OrderSummaryCard";

/** What the checkout's back is called in the tickets sheet. */
export const BACK_TO_TICKETS = "Back to tickets";

/**
 * The checkout before it is all there — the tickets being held, the
 * session loading, the card fields' page loading, the wallet being asked
 * about: its rows at the heights they will have, as Link in Bio's sheet
 * draws its checkout's shape (tickets-sheet.tsx `CheckoutShape`), so the
 * sheet sizes to the checkout once rather than growing with each part.
 * The button under it is the sheet's own ("Loading checkout"), not the
 * shape's.
 * The wallet's row is kept wherever the platform has a wallet; a device
 * without one set up gives it back once, when the checkout arrives.
 */
export function CheckoutShape({
  email,
  onBack,
  free = false,
  backLabel = BACK_TO_TICKETS,
  destination = "Tickets destination",
}: {
  /** The fan's address, when known: drawn as it will be. */
  email?: string | null;
  /** Back to the tickets; none while they are still being held. */
  onBack?: () => void;
  /** A free order: no wallet and no card to take. */
  free?: boolean;
  /** What going back is called (NativeCheckout's `backLabel`). */
  backLabel?: string;
  /** The address's label, as the checkout will draw it. */
  destination?: string;
}) {
  return (
    <View
      accessibilityLabel="Loading checkout"
      aria-busy
      testID="checkout-shape"
    >
      <View style={[styles.header, styles.gap12]}>
        <LPressable
          accessibilityLabel={backLabel}
          accessibilityRole="button"
          disabled={!onBack}
          hitSlop={10}
          onPress={onBack}
          sentryEventName="NativeCheckout.ShapeBack"
          style={styles.back}
        >
          <Chevron />
        </LPressable>
      </View>
      <View style={styles.gap12}>
        <OrderSummaryShape />
      </View>
      <View style={styles.gap16}>
        {email ? (
          <DestinationChip email={email} verified={false} label={destination} />
        ) : (
          <View style={styles.row}>
            <Text style={styles.label}>{destination}</Text>
            <View style={styles.pill} />
          </View>
        )}
      </View>
      {!free && walletOf(Platform.OS) ? (
        <View style={[styles.wallet, styles.gap12]}>
          <View style={styles.walletButton} />
          <View style={styles.divider}>
            <View style={styles.rule} />
            <Text style={styles.dividerText}>Or pay with card</Text>
            <View style={styles.rule} />
          </View>
        </View>
      ) : null}
      {free ? null : (
        <View style={[styles.fields, styles.gap12]}>
          <View style={styles.field} />
          <View style={styles.fieldRow}>
            <View style={styles.field} />
            <View style={styles.field} />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // The ready checkout's gaps (NativeCheckout's folds).
  gap12: { paddingBottom: 12 },
  gap16: { paddingBottom: 16 },
  header: { alignItems: "center", flexDirection: "row", minHeight: 28 },
  back: {
    alignItems: "center",
    height: 28,
    justifyContent: "center",
    marginLeft: -6,
    width: 28,
  },
  row: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  label: { color: colors.text, fontSize: 13, fontWeight: "600" },
  pill: {
    backgroundColor: colors.fill,
    borderRadius: 999,
    height: 26,
    width: 170,
  },
  wallet: { gap: 12 },
  walletButton: {
    backgroundColor: colors.fill,
    borderRadius: RADIUS,
    height: CONTROL_HEIGHT,
  },
  divider: { alignItems: "center", flexDirection: "row", gap: 10 },
  rule: { backgroundColor: colors.border, flex: 1, height: 1 },
  dividerText: { color: colors.secondary, fontSize: 13 },
  // The card page's fields, without the room it keeps round them.
  fields: { gap: 8, height: ISLAND_FIELDS_HEIGHT - 2 * ISLAND_EDGE + 12 },
  fieldRow: { flex: 1, flexDirection: "row", gap: 8 },
  field: {
    borderColor: colors.border,
    borderRadius: RADIUS,
    borderWidth: 1,
    flex: 1,
  },
});
